import React, { ComponentType, lazy, Suspense } from 'react'
import loadRestTranslations from 'parts/i18n/loadRestTranslations'
import { recoverFromChunkLoadError } from 'parts/utils/chunkLoadError'
import Loader from 'ui/Loader/Loader'

/**
 * Оборачивает страницу в динамический импорт, чтобы она не попадала в главный бандл.
 *
 * Каждая страница получает собственную границу Suspense, поэтому её загрузка
 * не подвешивает соседние роуты и работает там, где Suspense выше по дереву нет
 * (например, на роутах неавторизованного пользователя).
 *
 * Вместе с кодом страницы догружается основная часть словаря переводов,
 * поэтому страница монтируется, когда её ключи уже есть в i18next.
 *
 * Вызывать только на верхнем уровне модуля: React.lazy должен создаваться один раз.
 */
export default function lazyPage<Props extends object>(
	factory: () => Promise<{ default: ComponentType<Props> }>
): ComponentType<Props> {
	// TypeScript не умеет раскрывать пропсы обобщённого компонента в JSX,
	// поэтому типизация держится на сигнатуре самой функции lazyPage.
	const LazyComponent = lazy(() =>
		recoverFromChunkLoadError(() =>
			Promise.all([factory(), loadRestTranslations()]).then(
				([module]) => module
			)
		)
	) as ComponentType<any>

	return function LazyPage(props: Props) {
		return (
			<Suspense fallback={<Loader />}>
				<LazyComponent {...props} />
			</Suspense>
		)
	}
}
